import _ from 'lodash';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from 'src/store/store';
import { openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

import { Box } from '@mui/system';
import Card from '@mui/material/Card';
import Stack from '@mui/system/Stack';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { Avatar, MenuItem, Skeleton, IconButton, CardContent, Button } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useUploadFileMutation } from 'src/services/files/file.service';
import { useSetCandidateProfileMutation } from 'src/services/candidates/candidates.service';
import { generateCandidateProfileErrorMessage } from 'src/services/candidates/candidates.utils';
import {
  CandidateProfile,
  CandidateAvailabilityStatus,
} from 'src/services/candidates/candidates.types';

import Iconify from 'src/components/iconify';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { TITLE_OPTIONS } from 'src/utils/title-options';
import DateDisplay from 'src/components/date/date-display';
import FormProvider from 'src/components/hook-form/form-provider';
import ImageCropper from 'src/components/image-cropper/image-cropper';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';
import AvailabilityStatusLabel from 'src/components/candidates/availability-status-label';

import { TenantType } from 'src/types/enums';


type Props = {
  profile?: CandidateProfile;
  isLoading: boolean;
  showAvailabilityStatus?: boolean;
  mode?: 'edit' | 'view';
  onUpdate?: () => void;
  onImportProfile?: (profile: CandidateProfile) => void;
  onExportProfile?: (profile: CandidateProfile) => void;
  onAddToJob?: (profile: CandidateProfile) => void;
};

export default function ProfileUser({
  profile,
  isLoading,
  showAvailabilityStatus = true,
  mode = "edit",
  onUpdate,
  onImportProfile,
  onExportProfile,
  onAddToJob,
}: Props) {
  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const optionsPopover = usePopover();

  const [uploadFile] = useUploadFileMutation();

  const dispatch = useAppDispatch();

  const [photoURL, setPhotoURL] = useState<string>('');

  const [updateProfile] = useSetCandidateProfileMutation();

  const tenantType = useOrgTenant();

  const defaultValues = {
    title: '',
    first_name: '',
    last_name: '',
    profile_image_id: '',
    position_title: '',
  };

  const EditUserDetailsSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    position_title: Yup.string().max(60, t('validation.max_length', { length: 60 })).nullable(),
    profile_image_id: Yup.string().nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(EditUserDetailsSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, isDirty },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateProfile({
        id: profile?.id as string,
        data: {
          title: data.title || undefined,
          first_name: data.first_name as string,
          last_name: data.last_name as string,
          position_title: data.position_title as string,
          profile_image_id: data.profile_image_id ?? undefined,
          sections: [
            ...(profile?.sections?.map((rule) => ({
              type: rule.section_type,
              visibility_rule: rule.visibility_rule,
            })) ?? []),
          ],
        },
      }).unwrap();

      onUpdate?.();
    } catch (e) {
      console.log(e);

      enqueueSnackbar(generateCandidateProfileErrorMessage(e), { variant: 'error' });
    }
  });

  const onEditPhoto = useCallback(
    (imageId: string, publicUrl: string) => {
      setPhotoURL(publicUrl);
      setValue('profile_image_id', imageId);

      onSubmit();
    },
    [onSubmit, setValue]
  );

  useEffect(() => {
    if (!isLoading) {
      setPhotoURL(profile?.profile_image?.public_path ?? '');
      reset(
        {
          title: profile?.title ?? '',
          first_name: profile?.first_name ?? '',
          last_name: profile?.last_name ?? '',
          position_title: profile?.position_title ?? '',
        },
        { keepDirty: false }
      );
    }
  }, [isLoading, profile, reset, setValue]);

  const handleCopyToClipboard = (text: string) => {
    optionsPopover.onClose();

    if (!text) return;

    navigator.clipboard.writeText(text ?? "" as string);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  }

  const handleSendEmail = (row: CandidateProfile) => {
    if (!row.email) return;

    dispatch(openSingleMailerDialog({
      emails: [row.email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: row.id,
      targetType: MailerTargetType.CANDIDATE,
      targetId: row.id
    }));

  }

  if (mode === 'view') {
    return (
      <Card sx={{ margin: 'auto', boxShadow: 'unset' }}>
        <CardHeader sx={{ pb: 0 }} />
        <CardContent>
          <Stack direction="row" alignItems="start" justifyContent="space-between">
            <Avatar
              src={profile?.profile_image?.public_path}
              alt={`${profile?.first_name} ${profile?.last_name}`}
              sx={{ mr: 3, width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }}
            />

            <Stack direction="column" alignItems="start" justifyContent="center" flexGrow={1}>
              {isLoading ? (
                <Typography variant="h5">{t('common.loading')}</Typography>
              ) : (
                <>
                  <Typography variant="h5" textTransform="capitalize">
                    {`${profile?.first_name} ${profile?.last_name}`}
                  </Typography>
                  <Typography variant="caption" gutterBottom>
                    {!_.isEmpty(profile?.position_title)
                      ? _.capitalize(profile?.position_title ?? '')
                      : t('candidates.table.no_position_title')}
                  </Typography>

                  {[TenantType.Candidate, TenantType.Recruiter].includes(tenantType) && showAvailabilityStatus && (
                    <>
                      {profile?.availability_status ? (
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          alignItems="center"
                          justifyContent="start"
                          gap={0.5}
                        >
                          <AvailabilityStatusLabel status={profile?.availability_status} />
                          {profile?.availability_status ===
                            CandidateAvailabilityStatus.AVAILABLE_SOON &&
                            profile?.available_from && (
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <Typography variant="caption" noWrap>
                                  {t('candidates.table.available_on')}
                                </Typography>
                                <DateDisplay
                                  noWrap
                                  variant="caption"
                                  date={profile?.available_from}
                                  format="Do of MMM YYYY"
                                />
                              </Stack>
                            )}
                        </Stack>
                      ) : (
                        <FeatureLockedChip text={t('candidates.table.availability')} />
                      )}
                    </>
                  )}
                </>
              )}
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                endIcon={<Iconify icon="carbon:overflow-menu-horizontal" />}
                sx={{ mr: 1 }}
                onClick={optionsPopover.onOpen}
              >
                {t('common.actions')}
              </Button>
            </Box>
          </Stack>
        </CardContent>


        <CustomPopover
          open={optionsPopover.open}
          onClose={optionsPopover.onClose}
          arrow="top-right"
        >
          {
            profile?.email && (
              <MenuItem onClick={() => handleCopyToClipboard(profile?.email as string)}>
                <Iconify icon="mdi:email-outline" />
                <Typography variant='body2'>{t('candidates.detail.actions.copy_email')}</Typography>
              </MenuItem>
            )
          }

          {
            profile?.email && (
              <MenuItem
                onClick={() => {
                  optionsPopover.onClose();
                  handleSendEmail(profile);
                }}
              >
                <Iconify icon="lsicon:email-send-filled" />
                <Typography variant='body2'>{t('common.send_email')}</Typography>
              </MenuItem>
            )
          }

          {
            profile?.phone_number && (
              <MenuItem onClick={() => handleCopyToClipboard(profile?.phone_number as string)}>
                <Iconify icon="solar:phone-linear" />
                <Typography variant='body2'>{t('candidates.detail.actions.copy_phone')}</Typography>
              </MenuItem>
            )
          }

          {
            onImportProfile && (
              <MenuItem onClick={() => onImportProfile(profile as CandidateProfile)}>
                <Iconify icon="bx:import" />
                <Typography variant='body2'>{t('candidates.detail.actions.import_profile')}</Typography>
              </MenuItem>
            )
          }

          {
            onExportProfile && (
              <MenuItem onClick={() => onExportProfile(profile as CandidateProfile)}>
                <Iconify icon="bx:export" />
                <Typography variant='body2'>{t('candidates.detail.actions.export_profile')}</Typography>
              </MenuItem>
            )
          }

          {
            onAddToJob && (
              <MenuItem onClick={() => onAddToJob(profile as CandidateProfile)}>
                <Iconify icon="mdi:invite" />
                <Typography variant='body2'>{t('common.add_to_job')}</Typography>
              </MenuItem>
            )
          }
        </CustomPopover>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid xs={12} md={4}>
        <Card sx={{ pt: 4, pb: 4, px: 3, height: '100%', textAlign: 'center' }}>
          <ImageCropper
            onSubmit={onEditPhoto}
            existingUrl={photoURL}
            showGrid
            warningMessage={t('components.image_cropper.warning_message')}
          />
        </Card>
      </Grid>
      <Grid xs={12} md={8} spacing={2}>
        <Card sx={{ height: '100%' }}>
          <CardHeader

            title={
              isLoading ? (
                <Skeleton width={200} />
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  my={1}
                  gap={3}
                  justifyContent="space-between"
                >
                  <Typography variant="h6">{t('account.profile.details')}</Typography>

                  {!isLoading && (
                    <Stack direction="row" alignItems="center" justifyContent="start" gap={1}>
                      {[TenantType.Candidate, TenantType.Recruiter].includes(tenantType) && (
                        <Stack direction="column" alignItems="end" justifyContent="end" gap={0.5}>
                          <AvailabilityStatusLabel status={profile?.availability_status} />
                          {profile?.availability_status ===
                            CandidateAvailabilityStatus.AVAILABLE_SOON &&
                            profile?.available_from && (
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <Typography variant="caption">
                                  {t('candidates.table.available_on')}
                                </Typography>
                                <DateDisplay
                                  variant="caption"
                                  date={profile?.available_from}
                                  format="DD/MM/YYYY"
                                />
                              </Stack>
                            )}
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Stack>
              )
            }
          />
          <CardContent sx={{ pt: 4 }}>

            {isLoading ? (
              <Skeleton variant="rectangular" height={100} />
            ) : (
              <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={2}>
                  <Grid xs={12}>
                    <RHFSelect
                      name="title"
                      label={t('common.form_labels.title')}
                    >
                      <MenuItem value="">{t('common.none')}</MenuItem>
                      {TITLE_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(`enums.title_options.${value}`)}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Grid>

                  <Grid xs={6} sm={6}>
                    <RHFTextField
                      name="first_name"
                      label={t('common.form_labels.first_name')}
                      placeholder={t('common.placeholders.first_name')}
                    />
                  </Grid>

                  <Grid xs={6} sm={6}>
                    <RHFTextField
                      name="last_name"
                      label={t('common.form_labels.last_name')}
                      placeholder={t('common.placeholders.last_name')}
                    />
                  </Grid>

                  <Grid xs={12}>
                    <RHFTextField
                      name="position_title"
                      label={t('common.form_labels.position_title')}
                      placeholder={t('common.placeholders.position_title')}
                    />
                  </Grid>

                  <Grid xs={12} />
                </Grid>

                <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    disabled={!isDirty}
                  >
                    {t('common.save')}
                  </LoadingButton>
                </Stack>
              </FormProvider>
            )}
          </CardContent>
        </Card>

      </Grid>
    </Grid>
  );
}
